import { useEffect, useState } from 'react'
import { useAppStore } from '@/store/appStore'
import { runtimeEnv } from '@/utils/runtimeEnv'

// Probe URL mirrors apiClient.ts baseURL logic but targets /healthz (anonymous, no /api prefix).
const PROBE_URL = runtimeEnv.VITE_API_BASE_URL
  ? `${runtimeEnv.VITE_API_BASE_URL}/healthz`
  : '/healthz'

const PROBE_TIMEOUT_MS = 5_000
const PROBE_RETRY_DELAY_MS = 15_000

export function useConnectivity() {
  const { setTrulyOffline, isTrulyOffline } = useAppStore()
  const [initialized, setInitialized] = useState(false)

  // Effect 1: browser online/offline events for normal (local NIC) connectivity changes.
  useEffect(() => {
    const handleOnline = () => setTrulyOffline(false)
    const handleOffline = () => setTrulyOffline(true)

    setTrulyOffline(!navigator.onLine)
    setInitialized(true)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [setTrulyOffline])

  // Effect 2: upstream recovery probe loop.
  // When passive detection (apiClient interceptor) marks us offline but the OS
  // still reports a local connection (navigator.onLine=true), the browser's
  // 'online' event will never fire when upstream is restored. Poll /healthz
  // every 15s until it responds, then clear the offline state.
  useEffect(() => {
    if (!isTrulyOffline || !navigator.onLine) return

    let cancelled = false
    let retryTimerId: ReturnType<typeof setTimeout>

    const probe = async () => {
      while (!cancelled) {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS)

        try {
          const res = await fetch(PROBE_URL, {
            method: 'HEAD',
            signal: controller.signal,
            cache: 'no-store',
          })
          clearTimeout(timeoutId)

          if (!cancelled && res.ok) {
            setTrulyOffline(false)
            return
          }
        } catch {
          clearTimeout(timeoutId)
        }

        if (!cancelled) {
          await new Promise<void>((resolve) => {
            retryTimerId = window.setTimeout(resolve, PROBE_RETRY_DELAY_MS)
          })
        }
      }
    }

    probe()

    return () => {
      cancelled = true
      clearTimeout(retryTimerId)
    }
  }, [isTrulyOffline, setTrulyOffline])

  return { initialized }
}
